import { Injectable, inject } from '@angular/core';
import { Dialog } from '@capacitor/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { HapticsService } from './haptics.service';
import { PlatformService } from './platform.service';

@Injectable({ providedIn: 'root' })
export class NativeDialogService {
  private readonly platform = inject(PlatformService);
  private readonly haptics = inject(HapticsService);
  private readonly translocoService = inject(TranslocoService);

  async alert(title: string, message: string, buttonTitle?: string): Promise<void> {
    const okButton = buttonTitle ?? this.translocoService.translate('commonButtonsOk1');
    if (this.platform.isNativeMobile()) {
      await Dialog.alert({ title, message, buttonTitle: okButton });
    } else {
      window.alert(`${title}\n\n${message}`);
    }
  }

  async confirm(title: string, message: string, okButtonTitle?: string, cancelButtonTitle?: string): Promise<boolean> {
    const okButton = okButtonTitle ?? this.translocoService.translate('commonButtonsOk1');
    const cancelButton = cancelButtonTitle ?? this.translocoService.translate('commonButtonsCancel');
    this.haptics.warning();

    if (this.platform.isNativeMobile()) {
      const { value } = await Dialog.confirm({
        title,
        message,
        okButtonTitle: okButton,
        cancelButtonTitle: cancelButton,
      });
      return value;
    }

    return window.confirm(`${title}\n\n${message}`);
  }

  async prompt(
    title: string,
    message: string,
    okButtonTitle?: string,
    cancelButtonTitle?: string,
    inputPlaceholder?: string,
    inputText?: string
  ): Promise<string | null> {
    const okButton = okButtonTitle ?? this.translocoService.translate('commonButtonsOk1');
    const cancelButton = cancelButtonTitle ?? this.translocoService.translate('commonButtonsCancel');
    if (this.platform.isNativeMobile()) {
      const { value, cancelled } = await Dialog.prompt({
        title,
        message,
        okButtonTitle: okButton,
        cancelButtonTitle: cancelButton,
        inputPlaceholder,
        inputText,
      });
      return cancelled ? null : value;
    }

    const result = window.prompt(`${title}\n\n${message}`, inputText);
    return result;
  }

  async confirmDestructive(
    title: string,
    message: string,
    destructiveButtonTitle?: string,
    cancelButtonTitle?: string
  ): Promise<boolean> {
    const deleteButton = destructiveButtonTitle ?? this.translocoService.translate('commonButtonsDelete');
    const cancelButton = cancelButtonTitle ?? this.translocoService.translate('commonButtonsCancel');
    this.haptics.warning();

    return this.confirm(title, message, deleteButton, cancelButton);
  }

  async sessionTimeout(): Promise<void> {
    await this.alert(
      this.translocoService.translate('servicesDialogsSessionExpiredTitle'),
      this.translocoService.translate('servicesDialogsSessionExpiredMessage'),
      this.translocoService.translate('servicesButtonsLogIn')
    );
  }

  async networkError(): Promise<void> {
    this.haptics.error();
    await this.alert(
      this.translocoService.translate('servicesDialogsConnectionErrorTitle'),
      this.translocoService.translate('servicesDialogsConnectionErrorMessage')
    );
  }

  async confirmDiscardChanges(): Promise<boolean> {
    return this.confirm(
      this.translocoService.translate('modalsUnsavedChangesLeavePageTitle'),
      this.translocoService.translate('servicesDialogsUnsavedChangesMessage'),
      this.translocoService.translate('composeMessageDiscard'),
      this.translocoService.translate('composeMessageKeepEditing')
    );
  }

  async confirmDelete(itemName: string): Promise<boolean> {
    return this.confirmDestructive(
      this.translocoService.translate('servicesDialogsDeleteConfirmTitle', { itemName }),
      this.translocoService.translate('servicesDialogsDeleteConfirmMessage', { itemName: itemName.toLowerCase() })
    );
  }
}
